import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';

const emptyForm = { name: '', email: '', phone: '', address: '' };

const InvoiceContactsDialog = ({
  open,
  onClose,
  invoiceClient,
  companyId,
  contacts = [],
  onContactsChanged,
  showSnackbar
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [nameError, setNameError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteContact, setDeleteContact] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const normalizedSearch = search.trim().toLowerCase();
  const filteredContacts = useMemo(() => {
    if (!normalizedSearch) return contacts;
    return contacts.filter((contact) => (
      [contact.name, contact.email, contact.phone, contact.address]
        .some((value) => String(value ?? '').toLowerCase().includes(normalizedSearch))
    ));
  }, [contacts, normalizedSearch]);

  const resetForm = () => {
    setFormOpen(false);
    setEditingContact(null);
    setForm(emptyForm);
    setNameError('');
  };

  const resetDelete = () => {
    setDeleteContact(null);
  };

  const handleClose = () => {
    if (saving || deleting) return;
    setSearch('');
    resetForm();
    resetDelete();
    onClose();
  };

  const handleAdd = () => {
    setEditingContact(null);
    setForm(emptyForm);
    setNameError('');
    setFormOpen(true);
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setForm({
      name: contact.name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      address: contact.address || ''
    });
    setNameError('');
    setFormOpen(true);
  };

  const handleFieldChange = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    if (field === 'name') setNameError('');
  };

  const handleSubmit = async () => {
    const values = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      address: form.address.trim()
    };
    if (!values.name) {
      setNameError(t('Name is required.'));
      return;
    }
    if (!invoiceClient || !companyId || saving) return;

    const isEdit = Boolean(editingContact);
    setSaving(true);
    try {
      if (isEdit) {
        await invoiceClient.updateInvoiceContact(editingContact._id, values);
      } else {
        await invoiceClient.createInvoiceContact({ companyId, ...values });
      }
      await onContactsChanged();
      resetForm();
      showSnackbar(
        t(isEdit ? 'Custom invoice recipient updated successfully.' : 'Custom invoice recipient created successfully.'),
        'success'
      );
    } catch (error) {
      console.error(isEdit ? 'Error updating invoice contact' : 'Error creating invoice contact', error);
      showSnackbar(
        error.response?.data?.error || t(isEdit ? 'Failed to update custom invoice recipient.' : 'Failed to create custom invoice recipient.'),
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!invoiceClient || !companyId || !deleteContact || deleting) return;
    setDeleting(true);
    try {
      await invoiceClient.deleteInvoiceContact(deleteContact._id);
      await onContactsChanged();
      resetDelete();
      showSnackbar(t('Custom invoice recipient deleted successfully.'), 'success');
    } catch (error) {
      console.error('Error deleting invoice contact', error);
      showSnackbar(error.response?.data?.error || t('Failed to delete custom invoice recipient.'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const mutationsDisabled = !invoiceClient || !companyId || saving || deleting;

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>{t('Manage Custom Invoice Recipients')}</DialogTitle>
        <DialogContent>
          {/* Labels render above inputs in this theme, so the row must align
              to the bottom edge or the button lines up with the label. */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'flex-end' }} sx={{ my: 1 }}>
            <TextField
              fullWidth
              size="small"
              label={t('Search custom recipients')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              disabled={mutationsDisabled}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {t('Add Custom Recipient')}
            </Button>
          </Stack>

          {contacts.length === 0 ? (
            <Typography color="text.secondary">{t('No custom invoice recipients yet.')}</Typography>
          ) : filteredContacts.length === 0 ? (
            <Typography color="text.secondary">{t('No custom recipients match your search.')}</Typography>
          ) : filteredContacts.map((contact) => (
            <Box
              key={contact._id}
              sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1">{contact.name}</Typography>
                {[
                  ['email', contact.email],
                  ['phone', contact.phone],
                  ['address', contact.address]
                ].filter(([, value]) => Boolean(value)).map(([field, value]) => (
                  <Typography key={field} variant="body2" color="text.secondary">{value}</Typography>
                ))}
              </Box>
              <Tooltip title={t('Edit')}>
                <span>
                  <IconButton
                    aria-label={`${t('Edit')} ${contact.name}`}
                    onClick={() => handleEdit(contact)}
                    disabled={mutationsDisabled}
                  >
                    <EditIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('Delete')}>
                <span>
                  <IconButton
                    aria-label={`${t('Delete')} ${contact.name}`}
                    onClick={() => setDeleteContact(contact)}
                    disabled={mutationsDisabled}
                  >
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving || deleting}>{t('Close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={formOpen} onClose={saving ? undefined : resetForm} fullWidth maxWidth="sm">
        <DialogTitle>{t(editingContact ? 'Edit Custom Invoice Recipient' : 'Add Custom Invoice Recipient')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              label={t('Name')}
              value={form.name}
              onChange={handleFieldChange('name')}
              error={Boolean(nameError)}
              helperText={nameError}
            />
            <TextField fullWidth label={t('Email')} value={form.email} onChange={handleFieldChange('email')} />
            <TextField fullWidth label={t('Phone')} value={form.phone} onChange={handleFieldChange('phone')} />
            <TextField fullWidth label={t('Address')} value={form.address} onChange={handleFieldChange('address')} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetForm} disabled={saving}>{t('Cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={mutationsDisabled}>
            {saving ? <CircularProgress size={20} /> : t(editingContact ? 'Save' : 'Add Custom Recipient')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteContact)} onClose={deleting ? undefined : resetDelete} fullWidth maxWidth="sm">
        <DialogTitle>{t('Delete Custom Invoice Recipient')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('Are you sure you want to delete {{name}}? This action cannot be undone.', {
              name: deleteContact?.name || ''
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetDelete} disabled={deleting}>{t('Cancel')}</Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={mutationsDisabled}>
            {deleting ? <CircularProgress size={20} /> : t('Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InvoiceContactsDialog;
